import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../../context/AuthContext';
import { getSafetyBasicInfo, saveSafetyBasicInfo } from '../../../api/safetyAssessApi';
import OrgTreeSelect from '../../../components/OrgSelect/OrgTreeSelect';
import '../Safety.css';

function SafetyBasicInfo({ onSaved }) {
  const { token } = useContext(AuthContext);

  const [siteName, setSiteName] = useState('');
  const [managerName, setManagerName] = useState('');
  const [managerId, setManagerId] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [approvalType, setApprovalType] = useState('INTERNAL'); // 결재 방식: INTERNAL or EXTERNAL
  const [useProcess, setUseProcess] = useState(true); // 공정 사용 여부
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const fileInputRef = React.useRef(null);
  const [showOrgPopup, setShowOrgPopup] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchBasicInfo = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getSafetyBasicInfo(token);
      if (data) {
        if (data.siteName) setSiteName(data.siteName);
        if (data.managerName) setManagerName(data.managerName);
        if (data.managerId) setManagerId(data.managerId);
        if (data.ceoName) setCeoName(data.ceoName);
        if (data.zipcode) setZipcode(data.zipcode);
        if (data.address) setAddress(data.address);
        if (data.addressDetail) setAddressDetail(data.addressDetail);
        if (data.approvalType) setApprovalType(data.approvalType);
        if (data.useProcess !== undefined) setUseProcess(data.useProcess);
        if (data.logoUrl) setLogoUrl(data.logoUrl);
      }
    } catch (err) {
      console.error('기초 정보 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBasicInfo();
  }, [fetchBasicInfo]);

  const handleOrgConfirm = (selectedTargets) => {
    if (selectedTargets && selectedTargets.length > 0) {
      const target = selectedTargets[0];
      setManagerId(target.target_id);
      const name = target.target_name || '';
      const pos = target.position || '';
      const dept = target.dept_name || '';
      const formattedName = `${name}${pos ? ' ' + pos : ''}${dept ? '(' + dept + ')' : ''}`;
      setManagerName(formattedName);
    }
    setShowOrgPopup(false);
  };

  const handleLogoClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!siteName.trim()) {
      alert('사업장명을 입력해 주세요.');
      return;
    }
    if (!ceoName.trim()) {
      alert('대표자명을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('siteName', siteName.trim());
      formData.append('managerName', managerName);
      formData.append('managerId', managerId);
      formData.append('ceoName', ceoName.trim());
      formData.append('zipcode', zipcode.trim());
      formData.append('address', address.trim());
      formData.append('addressDetail', addressDetail.trim());
      formData.append('approvalType', approvalType);
      formData.append('useProcess', useProcess);
      if (logoFile) {
        formData.append('logoFile', logoFile);
      } else if (removeLogo) {
        formData.append('removeLogo', 'true');
      }

      await saveSafetyBasicInfo(formData, token);
      if (onSaved) onSaved();
      alert('사업장 기본 정보가 성공적으로 저장되었습니다.');
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sf-loading" style={{ padding: '60px 0' }}>
        <div className="sf-spinner"></div>
        <span className="sf-loading-text">기본 정보를 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeIn 0.2s ease-in-out', padding: '20px 0' }}>
      <form onSubmit={handleSave} className="sf-card" style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', borderRadius: '16px', backgroundColor: '#ffffff' }}>
        
        {/* 상단 헤더 묶음 박스 (타이틀 + 서브타이틀 + 저장 버튼) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', padding: '24px 30px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#1e293b', display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '5px', height: '22px', backgroundColor: '#3b82f6', borderRadius: '4px', marginRight: '12px' }}></div>
              사업장 기본 정보 설정
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '8px', marginLeft: '17px', letterSpacing: '-0.3px' }}>
              위험성평가 결과보고서 표지에 반영될 필수 정보와 로고 이미지를 관리합니다.
            </div>
          </div>
          <button
            type="submit"
            className="sf-btn sf-btn-primary sf-btn-pulse"
            disabled={saving}
            style={{ borderRadius: 8, padding: '0 28px', fontSize: '0.95rem', fontWeight: 700, height: '44px', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)' }}
          >
            {saving ? '저장 중...' : '기본 정보 저장하기'}
          </button>
        </div>

        <div className="row g-4">
          {/* 좌측: 로고 업로드 영역 */}
          <div className="col-md-4 d-flex flex-column align-items-center justify-content-start" style={{ borderRight: '1px solid #f1f5f9', padding: '10px 30px' }}>
            <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 16, display: 'block' }}>
              사업장 로고
            </label>
            <div style={{ position: 'relative' }}>
              <div
                onClick={handleLogoClick}
                style={{ width: 240, height: 120, borderRadius: '12px', border: '2px dashed #cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', cursor: 'pointer', background: '#f8fafc', transition: 'all 0.2s', overflow: 'hidden', padding: '10px' }}
              >
                {logoUrl ? (
                  <img src={logoUrl.startsWith('blob:') ? logoUrl : logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
                ) : (
                  <>
                    <i className="bi bi-camera" style={{ fontSize: '2rem', marginBottom: 8 }}></i>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>이미지 첨부 (가로형)</span>
                  </>
                )}
              </div>
              {logoUrl && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLogoUrl('');
                    setLogoFile(null);
                    setRemoveLogo(true);
                  }}
                  style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', padding: 0, margin: 0, lineHeight: 1 }}
                  title="로고 삭제"
                >
                  ✕
                </button>
              )}
            </div>
            <input type="file" ref={fileInputRef} onChange={handleLogoChange} accept="image/*" style={{ display: 'none' }} />
            <span style={{ fontSize: '0.75rem', color: 'var(--sf-text-muted)', textAlign: 'center', marginTop: 16, lineHeight: '1.4' }}>
              결과보고서 표지에 인쇄될<br />로고 이미지를 등록해 주세요.
            </span>
          </div>

          {/* 우측: 텍스트 입력 폼 */}
          <div className="col-md-8">
            <div className="d-flex flex-column gap-4" style={{ padding: '10px 0' }}>

              <div className="row g-3">
                {/* 사업장명 */}
                <div className="col-sm-7">
                  <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 6, display: 'block' }}>
                    사업장명 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                  </label>
                  <input
                    className="sf-input"
                    type="text"
                    placeholder="예: 테크앤아이코리아"
                    value={siteName}
                    onChange={e => setSiteName(e.target.value)}
                    required
                    style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                  />
                </div>
                {/* 대표자명 */}
                <div className="col-sm-5">
                  <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 6, display: 'block' }}>
                    대표자명 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                  </label>
                  <input
                    className="sf-input"
                    type="text"
                    placeholder="예: 홍길동"
                    value={ceoName}
                    onChange={e => setCeoName(e.target.value)}
                    style={{ backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                  />
                </div>
              </div>

              {/* 사업장 주소 */}
              <div>
                <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 6, display: 'block' }}>
                  사업장 주소 (소재지)
                </label>
                <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                  <input
                    className="sf-input"
                    type="text"
                    placeholder="우편번호"
                    value={zipcode}
                    onChange={e => setZipcode(e.target.value)}
                    style={{ width: '130px', height: '42px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                  />
                  <button
                    type="button"
                    className="sf-btn"
                    style={{ whiteSpace: 'nowrap', backgroundColor: '#475569', border: 'none', borderRadius: 6, height: '36px', padding: '0 14px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', color: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  className="sf-input"
                  type="text"
                  placeholder="기본 주소 입력"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  style={{ marginBottom: 10, height: '42px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                />
                <input
                  className="sf-input"
                  type="text"
                  placeholder="상세 주소 입력"
                  value={addressDetail}
                  onChange={e => setAddressDetail(e.target.value)}
                  style={{ height: '42px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' }}
                />
              </div>

              {/* 안전관리자 */}
              <div>
                <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 6, display: 'block' }}>
                  안전관리자 (평가 총괄 담당자)
                </label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    className="sf-input"
                    type="text"
                    value={managerName || ''}
                    placeholder="조직도 선택을 통해 지정해 주세요"
                    readOnly
                    style={{ flex: 1, cursor: 'pointer', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', height: '42px' }}
                    onClick={() => setShowOrgPopup(true)}
                  />
                  <button
                    type="button"
                    className="sf-btn"
                    onClick={() => setShowOrgPopup(true)}
                    style={{ whiteSpace: 'nowrap', backgroundColor: '#475569', border: 'none', borderRadius: 6, height: '36px', padding: '0 14px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', color: '#ffffff', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
                  >
                    조직도 선택
                  </button>
                </div>
              </div>

              {/* 공정 사용 여부 설정 */}
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 10, display: 'block' }}>
                  위험성평가 공정 사용 여부 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                    <input
                      type="radio"
                      name="useProcess"
                      checked={useProcess === true}
                      onChange={() => setUseProcess(true)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: useProcess === true ? '700' : '500' }}>공정 사용</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                    <input
                      type="radio"
                      name="useProcess"
                      checked={useProcess === false}
                      onChange={() => setUseProcess(false)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: useProcess === false ? '700' : '500' }}>공정 미사용</span>
                  </label>
                </div>
              </div>

              {/* 결재 방식 설정 */}
              <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--sf-text-secondary)', marginBottom: 10, display: 'block' }}>
                  위험성평가 결재 방식 <span style={{ color: 'var(--sf-danger)' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                    <input
                      type="radio"
                      name="approvalType"
                      value="INTERNAL"
                      checked={approvalType === 'INTERNAL'}
                      onChange={() => setApprovalType('INTERNAL')}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: approvalType === 'INTERNAL' ? '700' : '500' }}>위험성평가 자체결재</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                    <input
                      type="radio"
                      name="approvalType"
                      value="EXTERNAL"
                      checked={approvalType === 'EXTERNAL'}
                      onChange={() => setApprovalType('EXTERNAL')}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: approvalType === 'EXTERNAL' ? '700' : '500' }}>그룹웨어 전자결재</span>
                  </label>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '6px' }}>
                  * 자체결재: 이 시스템 내에서 기안-검토-승인 절차 진행<br/>
                  * 전자결재: 시스템 외부의 사내 그룹웨어를 통해 공식 결재 진행
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>

      {/* 조직도 팝업 포탈 */}
      {showOrgPopup && ReactDOM.createPortal(
        <div onClick={e => e.stopPropagation()}>
          <OrgTreeSelect
            selectionMode="single"
            targetType="user"
            initialSelected={managerId ? (() => {
              const fullName = managerName || '';
              const regex = /^([가-힣a-zA-Z0-9]+)(?:\s+([가-힣a-zA-Z0-9]+))?(?:\((.+)\))?$/;
              const match = fullName.trim().match(regex);
              let name = fullName;
              let position = '';
              let dept = '';
              let disp = fullName;
              if (match) {
                name = match[1] || '';
                position = match[2] || '';
                dept = match[3] || '';
                disp = `${name}${position ? ' / ' + position : ''}${dept ? ' (' + dept + ')' : ''}`;
              }
              return [{
                target_type: 'user',
                target_id: managerId,
                target_name: name,
                position: position,
                dept_name: dept,
                disp_target_name: disp
              }];
            })() : []}
            onConfirm={handleOrgConfirm}
            onCancel={() => setShowOrgPopup(false)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

export default SafetyBasicInfo;
